import { useEffect, useMemo, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import VideoCard from '@/components/feature/VideoCard';
import { videoList, typeLabels } from '@/mocks/videos';
import { filterVideos, getLatest } from '@/utils/video';

const typeFilters = [
  { key: 'all', label: '全部' },
  { key: 'movie', label: '电影' },
  { key: 'tv', label: '电视剧' },
  { key: 'variety', label: '综艺' },
];

const hotKeywords = ['星海远征', '风起云岚', '谜城档案', '欢乐总动员', '古装', '悬疑'];

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryParam = searchParams.get('query') || '';
  const [keyword, setKeyword] = useState(queryParam);
  const [activeType, setActiveType] = useState('all');

  const results = useMemo(() => {
    if (!queryParam.trim()) return [];
    return filterVideos(videoList, {
      subType: '全部',
      region: '全部',
      year: '全部',
      type: activeType === 'all' ? undefined : activeType,
      keyword: queryParam,
    });
  }, [queryParam, activeType]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchParams(keyword.trim() ? { query: keyword.trim() } : {});
  };

  const recommended = getLatest(videoList, 6);

  useEffect(() => {
    document.title = queryParam.trim()
      ? `「${queryParam}」搜索结果 - 在线免费观看`
      : '搜索 - 在线免费观看';
  }, [queryParam]);

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <SiteHeader />

      <main className="flex-1 w-full max-w-[1280px] mx-auto px-4 md:px-8 py-8">
        <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400"></i>
            <input
              type="text"
              name="query"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索影视剧名称、演员、导演或标签"
              className="w-full h-12 pl-11 pr-4 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300"
            />
          </div>
          <button
            type="submit"
            className="h-12 px-8 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
          >
            搜索
          </button>
        </form>

        {queryParam.trim() ? (
          <>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
              <p className="text-sm text-foreground-600">
                “<span className="text-primary-700 font-medium">{queryParam}</span>” 的搜索结果，共
                <span className="text-primary-700 font-semibold"> {results.length} </span>条
              </p>
              <div className="flex items-center gap-1 bg-background-100 rounded-full p-1">
                {typeFilters.map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() => setActiveType(item.key)}
                    className={`px-3 py-1.5 rounded-full text-xs transition-colors cursor-pointer whitespace-nowrap ${
                      activeType === item.key
                        ? 'bg-primary-500 text-background-50'
                        : 'text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>

            {results.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5">
                {results.map((item) => (
                  <VideoCard key={item.id} video={item} showDescription />
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <span className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
                  <i className="ri-search-eye-line text-3xl"></i>
                </span>
                <p className="text-foreground-700 font-medium mb-1">没有找到相关内容</p>
                <p className="text-sm text-foreground-500 mb-6">换个关键词试试，或参考下方热门搜索</p>
                <div className="flex flex-wrap justify-center gap-2 mb-10">
                  {hotKeywords.map((word) => (
                    <button
                      key={word}
                      type="button"
                      onClick={() => {
                        setKeyword(word);
                        setSearchParams({ query: word });
                      }}
                      className="px-3 py-1.5 rounded-full bg-background-100 text-xs text-foreground-600 hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      {word}
                    </button>
                  ))}
                </div>
                <div className="w-full">
                  <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-4 text-left">
                    <a href="#recommend" className="cursor-pointer">猜你喜欢</a>
                  </h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-4 md:gap-5">
                    {recommended.map((item) => (
                      <VideoCard key={item.id} video={item} />
                    ))}
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="flex flex-col items-center py-16 text-center">
            <span className="w-16 h-16 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mb-4">
              <i className="ri-search-line text-3xl"></i>
            </span>
            <p className="text-foreground-700 font-medium mb-1">输入关键词，发现你想看的内容</p>
            <p className="text-sm text-foreground-500 mb-6">支持按标题、演员、导演、标签进行全文匹配</p>
            <div className="flex flex-wrap justify-center gap-2">
              {hotKeywords.map((word) => (
                <button
                  key={word}
                  type="button"
                  onClick={() => {
                    setKeyword(word);
                    setSearchParams({ query: word });
                  }}
                  className="px-3 py-1.5 rounded-full bg-background-100 text-xs text-foreground-600 hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {word}
                </button>
              ))}
            </div>
          </div>
        )}

        <p className="mt-10 text-xs text-foreground-400 text-center">
          没有找到想看的内容？<Link to="/about" className="text-primary-600 hover:text-primary-700 cursor-pointer">了解本站资源说明</Link>
        </p>
      </main>

      <SiteFooter />
    </div>
  );
}